import type { LocalDate } from '../dates';
import type { Mutation, MutationOp } from '../mutations';
import type { BuildCtx } from '../spending/types';

export const WEIGHT_UNITS = ['kg', 'lb'] as const;
export type WeightUnit = (typeof WEIGHT_UNITS)[number];

export function isWeightUnit(value: unknown): value is WeightUnit {
  return value === 'kg' || value === 'lb';
}

/** One weigh-in. Stored as integer grams plus the unit it was typed in (conventions §3). */
export interface WeightEntryLike {
  id: string;
  date: LocalDate;
  valueG: number;
  unit: WeightUnit;
  note: string | null;
  deletedAt: string | null;
}

/** The per-user settings singleton. A goal needs both goal weights. */
export interface WeightSettings {
  unit: WeightUnit;
  goalG: number | null;
  goalStartG: number | null;
  goalStartDate: LocalDate | null;
}

export const DEFAULT_WEIGHT_SETTINGS: WeightSettings = {
  unit: 'kg',
  goalG: null,
  goalStartG: null,
  goalStartDate: null,
};

export type WeightResult<T, E extends string> = ({ ok: true } & T) | { ok: false; error: E };

const APPLIER_KEYS = ['id', 'userId', 'createdAt', 'updatedAt', 'deletedAt', 'clock'] as const;

/** A mutation from Weight. Built directly so tests control ids through ctx.newId. */
export function weightMutation(
  ctx: BuildCtx,
  table: string,
  op: MutationOp,
  rowId: string,
  values?: Record<string, unknown>,
): Mutation {
  for (const k of APPLIER_KEYS) {
    if (values && k in values) throw new Error(`Mutation values must not include '${k}'`);
  }
  return {
    id: ctx.newId(),
    table,
    op,
    rowId,
    ...(values ? { values } : {}),
    at: ctx.now,
    source: 'weight',
  };
}
